/**
 * What an `Activity.type` means, in words a reader recognises.
 *
 * The column is free-form, written by whatever filed the entry, and both screens
 * that show it were printing the stored value: the juhuklient's Tegevused list
 * put "voicemail" in a badge and the client timeline used it as the entry's
 * heading. That reads as a leaked database value in Estonian and as a leaked
 * database value in English too.
 *
 * The list is the values the platform actually writes, verified against the
 * places that create one:
 *   - `crm-lead-bridge/leadBridge.service.ts`   -> "form"
 *   - `crm-calls/lib/voicemail-intake.ts`       -> "voicemail"
 *   - `crm-projects/lib/projectActivity.ts`     -> "note", "status_change", "invoiced"
 *   - `portal/lib/activity.ts`                  -> "portal"
 *   - `crm-monitoring` (the site monitor)       -> "site_monitor"
 *   - the activity endpoints on leads and clients -> whatever the caller sends
 *
 * The last of those is why the fallback exists rather than a union type: the
 * endpoint takes any string up to 50 characters, and older rows carry "note"
 * and "call" from imports. An unknown value is shown verbatim, the same rule
 * `leadSource.ts` follows and for the same reason - a raw value beats a missing
 * translation key rendered on screen.
 */

/** Every type with a translation under `activity.*`. */
export const ACTIVITY_TYPES = [
  "note",
  "call",
  "voicemail",
  "form",
  "email",
  "status_change",
  "meeting",
  // Written only by the project timeline, when a project's unbilled work goes
  // onto an invoice. Its own kind rather than a "note", because the two are read
  // differently: a note is somebody's account of what happened, and this is a
  // thing the platform did and can prove - the hours and the invoice number in
  // the body are facts, not a summary.
  "invoiced",
  // Written only by the client portal, when the CLIENT does something in it:
  // opens a ticket, replies to one, signs a contract. Its own kind because the
  // interesting part is not what was done but who did it - the customer, on
  // their own, without anybody at the desk being involved.
  "portal",
  // Written only by Saidiseire (customer site monitoring), once per confirmed
  // outage of a watched site, when it resolves: "down 12:04 - back 12:31
  // (27 min)". A thing the platform measured, like "invoiced", and the one
  // entry in the timeline that answers "was their site down last week".
  "site_monitor",
] as const;

export type ActivityType = (typeof ACTIVITY_TYPES)[number];

const KNOWN = new Set<string>(ACTIVITY_TYPES);

/** The label for a type, or the raw value when nothing here recognises it. */
export function activityTypeLabel(type: string, t: (key: string) => string): string {
  return KNOWN.has(type) ? t(`activity.${type}`) : type;
}
